import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Customer } from '@/types/customer-type';
import { OrderItemResponse, OrderResponse } from '@/types/order-type';
import { AssignUserDialog } from './AssignUserDialog';
import InvoicePreviewCard from './invoice';
import { AdjustmentMode, OrderAdjustmentDialog } from './OrderAdjustmentDialog';
import { PaymentDialog, PaymentDialogValues } from './PaymentDialog';
import { ReceiptPreviewCard } from './receipt';

interface OrderDetailsDialogsProps {
  // Invoice dialog
  isOpenningInvoiceDialog: boolean;
  setIsOpenningInvoiceDialog: (open: boolean) => void;
  autoPrint: boolean;
  customer: Customer | null;
  order: OrderResponse | null;
  orderItems: OrderItemResponse[];
  company: any;

  // Receipt dialog
  isOpenningReceipt: boolean;
  setIsOpenningReceipt: (open: boolean) => void;
  total: number;
  shippingFee: number;
  subtotal: number;

  // Payment dialog
  openPaymentDialog: boolean;
  setOpenPaymentDialog: (open: boolean) => void;
  isCOD: boolean;
  totalPaid: number;
  onConfirmPayment: (payload: PaymentDialogValues) => Promise<void>;

  // Adjustment dialog
  showAdjustDialog: boolean;
  setShowAdjustDialog: (open: boolean) => void;
  adjustMode: AdjustmentMode;
  submitting: boolean;
  onAdjustmentSubmit: (payload: {
    mode: AdjustmentMode;
    amount?: number;
    reason: string;
    restock?: boolean;
  }) => Promise<void>;

  // Assign user dialog
  showAssignUserDialog: boolean;
  setShowAssignUserDialog: (open: boolean) => void;
  onAssignUser: (userId: string, orderId: string) => Promise<void>;
}

export function OrderDetailsDialogs({
  isOpenningInvoiceDialog,
  setIsOpenningInvoiceDialog,
  autoPrint,
  customer,
  order,
  orderItems,
  company,
  isOpenningReceipt,
  setIsOpenningReceipt,
  total,
  shippingFee,
  subtotal,
  openPaymentDialog,
  setOpenPaymentDialog,
  isCOD,
  totalPaid,
  onConfirmPayment,
  showAdjustDialog,
  setShowAdjustDialog,
  adjustMode,
  submitting,
  onAdjustmentSubmit,
  showAssignUserDialog,
  setShowAssignUserDialog,
  onAssignUser,
}: OrderDetailsDialogsProps) {
  if (!order || !customer) return null;

  return (
    <>
      {/* Invoice Dialog */}
      <Dialog open={isOpenningInvoiceDialog} onOpenChange={setIsOpenningInvoiceDialog}>
        <DialogContent className="w-auto max-w-fit p-0 overflow-visible">
          <DialogTitle className="sr-only">Invoice for Order {order.orderNumber}</DialogTitle>
          <InvoicePreviewCard
            customer={customer}
            order={order}
            company={company}
            items={orderItems}
            autoPrint={autoPrint}
          />
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={isOpenningReceipt} onOpenChange={setIsOpenningReceipt}>
        <DialogContent className="p-0 overflow-visible">
          <DialogTitle className="sr-only">Receipt for Order {order.orderNumber}</DialogTitle>
          <ReceiptPreviewCard
            orderId={order.id}
          />
        </DialogContent>
      </Dialog>

      {/* Adjustment Dialog */}
      <OrderAdjustmentDialog
        open={showAdjustDialog}
        mode={adjustMode}
        onOpenChange={setShowAdjustDialog}
        order={order}
        orderItems={orderItems}
        submitting={submitting}
        customer={customer}
        onSubmit={onAdjustmentSubmit}
      />

      {/* Payment Dialog */}
      <PaymentDialog
        orderId={String(order.orderNumber)}
        totalAmount={total}
        paidAmount={totalPaid}
        open={openPaymentDialog}
        onOpenChange={setOpenPaymentDialog}
        onSubmit={onConfirmPayment}
        isCOD={isCOD}
      />

      {/* Assign User Dialog */}
      <AssignUserDialog
        open={showAssignUserDialog}
        onOpenChange={setShowAssignUserDialog}
        order={order}
        customer={customer}
        onSubmit={onAssignUser}
      />
    </>
  );
}
